import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  MdCheck,
  MdChevronLeft,
  MdChevronRight,
  MdImage,
  MdOutlineFileUpload,
  MdSearchOff,
} from "react-icons/md";
import toast from "react-hot-toast";
import SearchBar from "../SearchBar/SearchBar";
import ZoomButton from "../buttons/ZoomButton";
import EditButton from "../buttons/EditButton";
import styles from "../ModelBackgroundSelectorMobileView/ModelBackgroundSelectorMobileView.module.css";
import OptimizedImage from "../OptimizedImage/OptimizedImage";
import UserImageUploadModal from "../UserImageUploadModal/UserImageUploadModal";
import MobileZoomModal from "../MobileZoomModal/MobileZoomModal";
import InternalPagination from "../InternalPagination/InternalPagination";
import Toggle from "../Toggle/Toggle";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { useModelUpload } from "../../customHook/ModelSelector/useModelUpload";

const ExpandableTitle = ({ title, className = "" }) => {
  const [showFullTitle, setShowFullTitle] = useState(false);

  return (
    <span
      className={`text-xs font-semibold text-primary cursor-pointer leading-[16px] transition-all duration-200 min-w-0 flex-1 ${className}`}
      style={{
        display: showFullTitle ? "block" : "-webkit-box",
        WebkitLineClamp: showFullTitle ? "none" : 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        maxHeight: showFullTitle ? "none" : "32px",
      }}
      onClick={(e) => {
        e.stopPropagation();
        setShowFullTitle((prev) => !prev);
      }}
      title={title}
    >
      {title}
    </span>
  );
};

const MobileModelCard = ({
  item,
  idx,
  selectionMode,
  isActive,
  userRole,
  handleSelectItem,
  openZoomModal,
  handleToggleModel,
  onEditModel,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const getImages = (it) => {
    if (Array.isArray(it?.images) && it.images.length > 0) {
      return it.images.filter(Boolean);
    }
    const imgs = [it?.front || it?.image, it?.back].filter(Boolean);
    return imgs;
  };

  const images = getImages(item);
  const currentImg = images[currentImageIndex] || images[0] || "";
  const hasMultipleImages = images.length > 1;
  const itemName = item?.name || item?.title || "Untitled";

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div
      onClick={() => handleSelectItem(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleSelectItem(item);
        }
      }}
      className={`${styles.optionCard} ${isActive ? styles.optionCardActive : ""} cursor-pointer`}
    >
      {isActive && (
        <span className={`${styles.checkBadge} !left-1.5 !right-auto`}>
          <MdCheck size={12} />
        </span>
      )}

      {/* Edit Button - Visible in Admin mode on top right */}
      {userRole === "admin" && onEditModel && (
        <EditButton
          onClick={(e) => {
            e.stopPropagation();
            onEditModel(item);
          }}
          positionClasses="absolute top-1.5 right-1.5 z-10"
          size="sm"
        />
      )}

      <div
        className={`${styles.optionImage} ${
          selectionMode === "user" ? styles.containImage : ""
        }`}
      >
        {currentImg ? (
          <OptimizedImage src={currentImg} alt={itemName} priority={idx < 4} />
        ) : (
          <MdImage size={28} />
        )}

        {/* Left Arrow Button */}
        {hasMultipleImages && (
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-5.5 h-5.5 rounded-full bg-white/90 backdrop-blur-sm text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-all cursor-pointer"
            aria-label="Previous image"
          >
            <MdChevronLeft size={16} />
          </button>
        )}

        {/* Right Arrow Button */}
        {hasMultipleImages && (
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-5.5 h-5.5 rounded-full bg-white/90 backdrop-blur-sm text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-all cursor-pointer"
            aria-label="Next image"
          >
            <MdChevronRight size={16} />
          </button>
        )}

        {/* Dot Indicators */}
        {hasMultipleImages && (
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {images.map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                  i === currentImageIndex ? "bg-primary" : "bg-white/70"
                }`}
              />
            ))}
          </div>
        )}

        {currentImg && (
          <ZoomButton
            onClick={(e) => {
              if (e) e.stopPropagation();
              const nextImg =
                images[(currentImageIndex + 1) % images.length] ||
                item.back ||
                "";
              openZoomModal(currentImg, nextImg);
            }}
            positionClasses="absolute bottom-1.5 right-1.5"
            size="md"
          />
        )}
      </div>

      <div className={`${styles.optionMeta} flex items-center justify-between gap-1.5`}>
        <ExpandableTitle title={itemName} />
        {item.tier && <b>{item.tier}</b>}
        {userRole === "admin" && (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <Toggle
              checked={
                item.bIsActive === 1 ||
                item.bIsActive === true ||
                (item.bIsActive === undefined &&
                  (item.isActive === 1 ||
                    item.isActive === true ||
                    item.isActive === undefined))
              }
              onChange={() => handleToggleModel(item)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ModelSelectorMobileView = ({
  filteredData = [],
  searchTerm,
  onSearchChange,
  setStep,
  step,
  setSelectedModel,
  selectedModel,
  selectedProduct,
  selectionMode = "user",
  onSelectionModeChange = null,
  onRefreshModels = null,
  isLoading = false,
  pagination = null,
  onPageChange = null,
}) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const { uploadModel, isLoading: isModelUploadLoading } = useModelUpload();
  const { role: userRole, token } = useSelector((state) => state.auth);
  const [, setToggleTick] = useState(0);
  const forceUpdate = () => setToggleTick((prev) => prev + 1);

  const [isMobileZoomOpen, setIsMobileZoomOpen] = useState(false);
  const [zoomImage, setZoomImage] = useState({ front: "", back: "" });

  const openZoomModal = (front, back = "") => {
    setZoomImage({ front, back });
    setIsMobileZoomOpen(true);
  };

  const closeZoomModal = () => {
    setZoomImage({ front: "", back: "" });
    setIsMobileZoomOpen(false);
  };

  const activeItem = selectedModel;
  const pageTitle = "Select Model";
  const pageDescription = "Choose the model you want to try the garment on.";

  const handleUploadModalOpen = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadSubmit = async (dataPayload) => {
    try {
      const result = await uploadModel(dataPayload);

      if (result.success) {
        toast.success("Image uploaded successfully!");
        handleUploadModalClose();
        if (onRefreshModels) {
          onRefreshModels();
        }
      } else {
        toast.error(`Upload failed: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Upload error: ${error.message}`);
    }
  };

  const handleToggleModel = async (item) => {
    const itemId = item.id ?? item.iModelId ?? item.modelId;
    if (!itemId) return;
    const currentActiveVal = item.bIsActive ?? item.isActive;
    const previousState =
      currentActiveVal === 1 ||
      currentActiveVal === true ||
      currentActiveVal === undefined;
    const newState = !previousState;

    item.bIsActive = newState ? 1 : 0;
    item.isActive = newState ? 1 : 0;
    forceUpdate();

    try {
      const response = await apiHandler({
        url: `${endpoint.TOGGLE_MODEL}/${itemId}/toggle`,
        method: "PATCH",
        authToken: { token },
        noCache: true,
      });

      if (
        (response?.status && response.status >= 400) ||
        response?.data?.status === "error" ||
        response?.data?.success === false
      ) {
        throw new Error(
          response?.data?.message || "Failed to update model status",
        );
      }

      const resData = response?.data?.data || response?.data;
      if (resData) {
        const activeVal = resData.bIsActive ?? resData.isActive;
        if (activeVal !== undefined && activeVal !== null) {
          const finalActive = activeVal === 1 || activeVal === true ? 1 : 0;
          item.bIsActive = finalActive;
          item.isActive = finalActive;
        }
      }

      forceUpdate();
      toast.success(
        response?.data?.message ||
          (newState
            ? "Model enabled successfully"
            : "Model disabled successfully"),
      );
    } catch (err) {
      item.bIsActive = previousState ? 1 : 0;
      item.isActive = previousState ? 1 : 0;
      forceUpdate();
      toast.error(err?.message || "Failed to update model status");
    }
  };

  const handleSelectItem = (item) => {
    setSelectedModel(item);
  };

  const getItemImage = (item) =>
    item?.front || item?.images?.[0] || item?.image || "";
  const getItemName = (item) => item?.name || item?.title || "Untitled";

  const uploadAllowed = selectionMode === "user";

  return (
    <section className={styles.mobileShell}>
      <div className={styles.mobileHeaderBlock}>
        <div className={styles.mobileTitleRow}>
          <div className={styles.mobileTitleCopy}>
            <h2>{pageTitle}</h2>
            <p>{pageDescription}</p>
          </div>
        </div>

        <div className={styles.searchUploadRow}>
          <div className={styles.searchWrap}>
            <SearchBar
              value={searchTerm}
              onChange={onSearchChange}
              placeholder="Search models..."
            />
          </div>

          {uploadAllowed && (
            <button
              onClick={handleUploadModalOpen}
              className={styles.uploadIconButton}
              type="button"
              aria-label="Upload image"
              title="Upload image"
            >
              <MdOutlineFileUpload size={16} />
              <span>Upload</span>
            </button>
          )}
        </div>

        {onSelectionModeChange && (
          <div className={styles.segmentedTabs}>
            <button
              type="button"
              onClick={() => onSelectionModeChange("model")}
              className={
                selectionMode === "model"
                  ? styles.segmentedTabActive
                  : styles.segmentedTab
              }
            >
              Studio Models
            </button>
            <button
              type="button"
              onClick={() => onSelectionModeChange("user")}
              className={
                selectionMode === "user"
                  ? styles.segmentedTabActive
                  : styles.segmentedTab
              }
            >
              Users
            </button>
          </div>
        )}
      </div>

      {selectedProduct && (
        <section className={styles.selectedProductCard}>
          <div className={styles.selectedProductImage}>
            <OptimizedImage
              src={selectedProduct.images?.[0]}
              alt={
                selectedProduct.name ||
                selectedProduct.title ||
                "Selected product"
              }
              priority
            />
            <ZoomButton
              onClick={() =>
                openZoomModal(
                  selectedProduct.images?.[0],
                  selectedProduct.images?.[1],
                )
              }
            />
          </div>
          <div className={styles.selectedProductCopy}>
            <span>Selected Product</span>
            <h3>
              {selectedProduct.name || selectedProduct.title || "Product"}
            </h3>
            <p>
              {selectedProduct.modelType === "mannequin"
                ? "Mannequin"
                : "Model"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setStep(1)}
            className={styles.changeButton}
          >
            Change
          </button>
        </section>
      )}

      <section className={styles.catalogPanel}>
        <div className={styles.catalogHeader}>
          <h3>Models</h3>
          {activeItem ? (
            <span>Selected: {getItemName(activeItem)}</span>
          ) : (
            <span>No selection</span>
          )}
        </div>

        {isLoading ? (
          <div className={styles.emptyState}>Loading options...</div>
        ) : filteredData.length === 0 ? (
          <div className={styles.emptyState}>
            <MdSearchOff size={32} />
            <p>No results found</p>
          </div>
        ) : (
          <div className={styles.optionGrid}>
            {filteredData.map((item, idx) => {
              const isActive = selectedModel?.name === item.name;

              return (
                <MobileModelCard
                  key={
                    item.name || item.title || item.id || item.iModelId || idx
                  }
                  item={item}
                  idx={idx}
                  selectionMode={selectionMode}
                  isActive={isActive}
                  userRole={userRole}
                  handleSelectItem={handleSelectItem}
                  openZoomModal={openZoomModal}
                  handleToggleModel={handleToggleModel}
                />
              );
            })}
          </div>
        )}
        {pagination && pagination.total > 0 && (
          <InternalPagination
            className={styles.catalogPagination}
            page={pagination.page}
            limit={pagination.limit}
            total={pagination.total}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
          />
        )}
      </section>

      <footer className={styles.mobileFooter}>
        <button
          type="button"
          onClick={() => setStep(step - 1)}
          className={styles.footerBackButton}
        >
          Back
        </button>

        <div className={styles.footerSelection}>
          {activeItem ? (
            <>
              <span className={styles.footerLabel}>Selected:</span>
              <div className={styles.footerThumb}>
                {getItemImage(activeItem) ? (
                  <img
                    src={getItemImage(activeItem)}
                    alt={getItemName(activeItem)}
                  />
                ) : (
                  <MdImage size={16} />
                )}
              </div>
              <span className={styles.footerName}>
                {getItemName(activeItem)}
              </span>
            </>
          ) : (
            <span className={styles.footerEmpty}>No selection</span>
          )}
        </div>

        <button
          type="button"
          onClick={() => setStep(step + 1)}
          disabled={!activeItem}
          className={styles.footerContinueButton}
        >
          Continue
        </button>
      </footer>

      <UserImageUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleUploadModalClose}
        onSubmit={handleUploadSubmit}
        isLoading={isModelUploadLoading}
        defaultGender={
          selectedProduct?.category === "men" ? "men" : "women"
        }
      />

      <MobileZoomModal
        isOpen={isMobileZoomOpen}
        onClose={closeZoomModal}
        front={zoomImage.front}
        back={zoomImage.back}
      />
    </section>
  );
};

export default ModelSelectorMobileView;
